/**
 * Intercepts the browser's native back gesture to close a modal instead of
 * navigating away. Pushes a dummy history state on open and listens for
 * popstate to close.
 *
 * Usage:
 *   - Call `this.openWithHistory()` to show the modal and push a history state.
 *   - Call `this.closeWithHistory()` to close it (also cleans up history if needed).
 *   - The exposed `isHistoryBackClosableActive` data property drives v-if / v-show.
 *   - Pass a unique `historyStateKey` option to avoid collisions when multiple
 *     instances coexist on the same page.
 */
export default {
  data() {
    return {
      isHistoryBackClosableActive: false,
    };
  },

  mounted() {
    window.addEventListener('popstate', this.onHistoryBackClosablePopState);
  },

  beforeDestroy() {
    window.removeEventListener('popstate', this.onHistoryBackClosablePopState);
  },

  methods: {
    openWithHistory() {
      this.isHistoryBackClosableActive = true;
      history.pushState(
        { historyBackClosable: this.$options.name ?? true },
        ''
      );
    },

    closeWithHistory() {
      if (!this.isHistoryBackClosableActive) {
        return;
      }

      // If our dummy state is still the current one, pop it before closing.
      if (history.state?.historyBackClosable) {
        history.back();
        // Closing is handled by _onHistoryBackClosablePopState after back() resolves.
        return;
      }

      this.isHistoryBackClosableActive = false;
      this.onHistoryBackClosableClosed();
    },

    /** Override in consuming component to react after the modal closes. */
    onHistoryBackClosableClosed() {},

    onHistoryBackClosablePopState() {
      if (this.isHistoryBackClosableActive) {
        this.isHistoryBackClosableActive = false;
        this.onHistoryBackClosableClosed();
      }
    },
  },
};
